import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";

type Props = {
  findingType: string;
  details: Record<string, any> | null | undefined;
  currency: string;
};

const fmtMoney = (n: any, currency = "USD") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(v);
};

const fmt = (v: any) => (v === null || v === undefined || v === "" ? "—" : String(v));

function Row({ label, before, after, money, currency }: { label: string; before: any; after: any; money?: boolean; currency: string }) {
  const changed = String(before ?? "") !== String(after ?? "");
  return (
    <div className={`grid grid-cols-[140px_1fr_auto_1fr] items-center gap-2 text-xs py-1 ${changed ? "" : "opacity-60"}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono ${changed ? "line-through text-destructive" : ""}`}>
        {money ? fmtMoney(before, currency) : fmt(before)}
      </span>
      <ArrowRight className="h-3 w-3 text-muted-foreground" />
      <span className={`font-mono ${changed ? "font-semibold text-emerald-700 dark:text-emerald-400" : ""}`}>
        {money ? fmtMoney(after, currency) : fmt(after)}
      </span>
    </div>
  );
}

export function FindingExplanation({ findingType, details, currency }: Props) {
  if (!details) {
    return <div className="text-xs text-muted-foreground">No additional detail available.</div>;
  }

  const changed: string[] = Array.isArray(details.changed_fields) ? details.changed_fields : [];

  return (
    <div className="space-y-3 bg-muted/40 rounded-md p-3 border">
      {changed.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <span className="text-xs text-muted-foreground mr-1">Changed:</span>
          {changed.map((f) => (
            <Badge key={f} variant="outline" className="text-[10px]">{f}</Badge>
          ))}
        </div>
      )}

      {findingType === "amendment_drift" && details.booked && details.active && (
        <div className="space-y-0.5">
          <div className="text-xs font-medium mb-1 grid grid-cols-[140px_1fr_auto_1fr] gap-2">
            <span></span><span className="text-muted-foreground">Booked</span><span /><span className="text-muted-foreground">Active fee on {fmt(details.event_date)}</span>
          </div>
          <Row label="Fee schedule" before={details.booked.fee_schedule_id} after={details.active.fee_schedule_id} currency={currency} />
          <Row label="Fee category" before={details.booked.fee_category} after={details.active.fee_category} currency={currency} />
          <Row label="Fee name" before={details.booked.fee_name} after={details.active.fee_name} currency={currency} />
          <Row label="Unit amount" before={details.booked.unit_amount} after={details.active.unit_amount} money currency={currency} />
          <Row label="Effective from" before={details.booked.effective_from} after={details.active.effective_from} currency={currency} />
          <Row label="Effective to" before={details.booked.effective_to} after={details.active.effective_to} currency={currency} />
          <Row label="× quantity" before={details.quantity} after={details.quantity} currency={currency} />
          <Row label="Line total" before={details.booked.total} after={details.active.total} money currency={currency} />
        </div>
      )}

      {findingType === "phantom_billing" && (
        <div className="space-y-1 text-xs">
          <div><span className="text-muted-foreground">Reason:</span> {fmt(details.reason)}</div>
          <div><span className="text-muted-foreground">Event date:</span> {fmt(details.event_date)}</div>
          <div><span className="text-muted-foreground">Linked visit:</span> {fmt(details.linked_visit_id)}</div>
          <div><span className="text-muted-foreground">Billed:</span> {fmtMoney(details.total_amount, currency)} ({fmt(details.quantity)} × {fmtMoney(details.unit_amount, currency)})</div>
        </div>
      )}

      {findingType === "duplicate_billing" && (
        <div className="space-y-1 text-xs">
          <Row label="Row count" before={details.duplicate_count} after={details.expected_count} currency={currency} />
          <Row label="Per-event amount" before={details.per_event_amount} after={details.per_event_amount} money currency={currency} />
          <Row label="Sum total" before={details.sum_total} after={details.per_event_amount} money currency={currency} />
          {Array.isArray(details.event_dates) && (
            <div><span className="text-muted-foreground">Dates:</span> {details.event_dates.join(", ")}</div>
          )}
        </div>
      )}

      {findingType === "out_of_window_visit" && (
        <div className="space-y-1 text-xs">
          <div><span className="text-muted-foreground">Visit:</span> {fmt(details.visit_code)} — {fmt(details.visit_name)}</div>
          <Row label="Scheduled" before={details.scheduled_date} after={details.scheduled_date} currency={currency} />
          <Row label="Actual" before={details.scheduled_date} after={details.actual_date} currency={currency} />
          <div><span className="text-muted-foreground">Window:</span> [{fmt(details.window_start)} → {fmt(details.window_end)}] (±{fmt(details.window_before_days)}/{fmt(details.window_after_days)} days)</div>
          <div><span className="text-muted-foreground">Days off:</span> {fmt(details.days_off)}</div>
        </div>
      )}

      {findingType === "missed_billable" && (
        <div className="space-y-1 text-xs">
          <div><span className="text-muted-foreground">Visit:</span> {fmt(details.visit_code)} — {fmt(details.visit_name)} on {fmt(details.actual_date)}</div>
          <Row label="Expected fee" before="(none billed)" after={details.expected_fee_name} currency={currency} />
          <Row label="Expected amount" before={0} after={details.expected_amount} money currency={currency} />
          <div><span className="text-muted-foreground">Visit status:</span> {fmt(details.visit_status)}</div>
        </div>
      )}

      {findingType === "missed_scheduled_visit" && (
        <div className="space-y-1 text-xs">
          <div><span className="text-muted-foreground">Visit:</span> {fmt(details.visit_type)}</div>
          <Row label="Scheduled" before={details.scheduled_date} after={details.scheduled_date} currency={currency} />
          <Row label="Status" before="completed (expected)" after={details.visit_status} currency={currency} />
          <div><span className="text-muted-foreground">Days overdue:</span> {fmt(details.days_overdue)}</div>
        </div>
      )}
    </div>
  );
}
